import { io } from 'socket.io-client';
import { API_ENDPOINT } from '../../util/constants';
import { getCollaborationSessionId } from '../../collaboration/sessionId';

/**
 * WebSocket Client
 * 
 * Manages WebSocket connection to the server for:
 * - Real-time data updates (juror:created, juror:updated, etc.)
 * - Presence awareness (who's viewing/editing)
 * - Case subscriptions
 */

let socket = null;
let connectionPromise = null;
const eventHandlers = new Map();
const subscribedCases = new Set();

/**
 * Get the authentication token for WebSocket connection
 */
async function getAuthToken() {
  // Current app auth is primarily cookie-based. Only attach token auth if a real token exists.
  const tokenKeys = ['auth-token', 'access-token', 'jwt', 'token'];
  for (const key of tokenKeys) {
    const value = localStorage.getItem(key);
    if (value && value.trim().length > 0) return value;
  }

  // Support optional JSON auth payloads if present.
  const authJsonKeys = ['auth', 'user', 'session'];
  for (const key of authJsonKeys) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      const token = parsed?.token || parsed?.accessToken || parsed?.jwt;
      if (token && String(token).trim().length > 0) return token;
    } catch (_e) {
      // ignore malformed JSON in unrelated keys
    }
  }

  return null;
}

/**
 * Connect to WebSocket server
 * @returns {Promise<Socket>}
 */
export async function connect() {
  if (socket?.connected) {
    return socket;
  }

  if (connectionPromise) {
    return connectionPromise;
  }

  connectionPromise = new Promise(async (resolve, reject) => {
    try {
      const token = await getAuthToken();
      const wsUrl = API_ENDPOINT;
      const socketOptions = {
        path: '/ws',
        transports: ['websocket', 'polling'],
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      };

      if (token) {
        socketOptions.auth = { token };
      } else {
        console.log('No WebSocket token found; using cookie-based auth');
      }

      socket = io(wsUrl, {
        ...socketOptions
      });

      socket.on('connect', () => {
        console.log('WebSocket connected');
        
        // Re-subscribe to any cases we were subscribed to
        for (const caseId of subscribedCases) {
          socket.emit('subscribe:case', {
            caseId,
            sessionId: getCollaborationSessionId(),
          });
        }
        
        resolve(socket);
      });

      socket.on('disconnect', (reason) => {
        console.log('WebSocket disconnected:', reason);
      });

      // Fires on every reconnect after a disconnect (not on initial connect)
      socket.io.on('reconnect', () => {
        console.log('WebSocket reconnected after disconnect');
        emitToHandlers('ws:reconnect', {});
      });

      socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error);
        if (!socket.connected) {
          reject(error);
          connectionPromise = null;
        }
      });

      socket.on('error', (error) => {
        console.error('WebSocket error:', error);
        emitToHandlers('error', error);
      });

      // Set up data event handlers
      setupDataHandlers(socket);
      setupPresenceHandlers(socket);

    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      reject(error);
      connectionPromise = null;
    }
  });

  return connectionPromise;
}

/**
 * Set up handlers for data synchronization events
 */
function setupDataHandlers(socket) {
  // Case updates
  socket.on('case:updated', (data) => {
    console.log('Received case update:', data.caseId);
    emitToHandlers('case:updated', data);
  });

  // Juror events
  socket.on('juror:created', (data) => {
    console.log('Received juror created:', data.juror?._id);
    emitToHandlers('juror:created', data);
  });

  socket.on('juror:updated', (data) => {
    console.log('Received juror update:', data.jurorId);
    emitToHandlers('juror:updated', data);
  });

  socket.on('juror:deleted', (data) => {
    console.log('Received juror deleted:', data.jurorId);
    emitToHandlers('juror:deleted', data);
  });

  socket.on('juror:batch', (data) => {
    console.log('Received juror batch update:', data.caseId);
    emitToHandlers('juror:batch', data);
  });
}

/**
 * Set up handlers for presence events
 */
function setupPresenceHandlers(socket) {
  socket.on('presence:update', (presence) => {
    emitToHandlers('presence:update', presence);
  });

  socket.on('user:editing', (data) => {
    emitToHandlers('user:editing', data);
  });

  socket.on('user:stopped', (data) => {
    emitToHandlers('user:stopped', data);
  });

  socket.on('juror:live-state', (data) => {
    emitToHandlers('juror:live-state', data);
  });
}

/**
 * Emit event to all registered handlers
 */
function emitToHandlers(event, data) {
  const handlers = eventHandlers.get(event) || [];
  for (const handler of handlers) {
    try {
      handler(data);
    } catch (e) {
      console.error(`Error in ${event} handler:`, e);
    }
  }
}

/**
 * Register an event handler
 * @param {string} event - Event name
 * @param {Function} handler - Handler function
 * @returns {Function} - Unsubscribe function
 */
export function on(event, handler) {
  if (!eventHandlers.has(event)) {
    eventHandlers.set(event, []);
  }
  eventHandlers.get(event).push(handler);

  // Return unsubscribe function
  return () => {
    const handlers = eventHandlers.get(event);
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }
  };
}

/**
 * Remove an event handler
 */
export function off(event, handler) {
  const handlers = eventHandlers.get(event);
  if (handlers) {
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }
  }
}

/**
 * Subscribe to a case for real-time updates
 * @param {string} caseId - Case ID
 */
export async function subscribeToCase(caseId) {
  subscribedCases.add(caseId);
  
  if (socket?.connected) {
    socket.emit('subscribe:case', {
      caseId,
      sessionId: getCollaborationSessionId(),
    });
    console.log(`Subscribed to case: ${caseId}`);
  } else {
    // Will subscribe after connection
    try {
      await connect();
      socket.emit('subscribe:case', {
        caseId,
        sessionId: getCollaborationSessionId(),
      });
      console.log(`Subscribed to case: ${caseId}`);
    } catch (e) {
      console.warn('Could not subscribe to case (not connected):', caseId);
    }
  }
}

/**
 * Unsubscribe from a case
 * @param {string} caseId - Case ID
 */
export function unsubscribeFromCase(caseId) {
  subscribedCases.delete(caseId);
  
  if (socket?.connected) {
    socket.emit('unsubscribe:case', caseId);
    console.log(`Unsubscribed from case: ${caseId}`);
  }
}

/**
 * Announce that current user is editing a juror
 * @param {string} caseId - Case ID
 * @param {string} jurorId - Juror ID being edited
 */
export function announceEditing(caseId, jurorId) {
  if (socket?.connected) {
    socket.emit('editing:juror', {
      caseId,
      jurorId,
      sessionId: getCollaborationSessionId(),
    });
  }
}

/**
 * Announce that current user stopped editing
 * @param {string} caseId - Case ID
 * @param {string} jurorId - Juror ID that was being edited
 */
export function announceStoppedEditing(caseId, jurorId) {
  if (socket?.connected) {
    socket.emit('stop:editing', {
      caseId,
      jurorId,
      sessionId: getCollaborationSessionId(),
    });
  }
}

/**
 * Broadcast ephemeral live typing state for a juror field
 * @param {Object} payload - Live state payload
 */
export function emitLiveState(payload) {
  if (socket?.connected && payload?.caseId && payload?.jurorId) {
    socket.emit('juror:live-state', {
      ...payload,
      sessionId: payload.sessionId || getCollaborationSessionId(),
    });
  }
}

/**
 * Check if WebSocket is connected
 * @returns {boolean}
 */
export function isConnected() {
  return socket?.connected || false;
}

/**
 * Disconnect WebSocket
 */
export function disconnect() {
  if (socket) {
    socket.disconnect();
    socket = null;
    connectionPromise = null;
    subscribedCases.clear();
    console.log('WebSocket disconnected');
  }
}

/**
 * Get connection status
 * @returns {'connected' | 'connecting' | 'disconnected'}
 */
export function getStatus() {
  if (socket?.connected) return 'connected';
  if (connectionPromise) return 'connecting';
  return 'disconnected';
}

export default {
  connect,
  disconnect,
  on,
  off,
  subscribeToCase,
  unsubscribeFromCase,
  announceEditing,
  announceStoppedEditing,
  emitLiveState,
  isConnected,
  getStatus
};
